import React, { useState, useRef } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';

import Navigation from '../components/Navigation';
import ProjectList from '../components/Project/ProjectList';
import { AssistantChat } from '../components/Assistant/AssistantChat';
import { GET_ALL_USERS } from '../graphql/GET_ALL_USERS';
import { GET_PROJECTS_BY_FILTER } from '../graphql/GET_PROJECTS_BY_FILTER';
import { useHistory } from 'react-router-dom';
import { FilteredProjectListContextProvider } from '../contexts/filteredProjectListContext';
import { IClientUser } from '../interfaces/user';

import { GET_ALL_GENRES } from '../graphql/GET_ALL_GENRES';
import { GET_ALL_LOCATIONS } from '../graphql/GET_ALL_LOCATIONS';
import feedbackImg from '../assets/svg/feedback.svg';
// import { accessTokenStorage } from '../services/auth';
import notificationIcon from '../assets/svg/notification.svg';
import videoCameraIcon from '../assets/svg/video-camera.svg';
import folderIcon from '../assets/svg/folder.svg';
import searchIcon from '../assets/svg/search.svg';
import filterIcon from '../assets/svg/Icon-Filter.svg';
import pinIcon from '../assets/svg/pin.svg';
import iconFilm from '../assets/svg/Icon-Film.svg';
import iconCinema from '../assets/svg/Icon-Cinema.svg';
import iconComposer from '../assets/svg/Icon-Composer.svg';
import iconGame from '../assets/svg/Icon-Console.svg';
import iconUXUI from '../assets/svg/Icon-UXUI.svg';
import iconCostume from '../assets/svg/Icon-Costume.svg';
import iconCamera from '../assets/svg/camera.svg';
import iconPalette from '../assets/svg/Icon-Palette.svg';
import iconLove from '../assets/svg/Icon-Love.svg';
import iconPen from '../assets/svg/landing/icon-pen.svg';

interface FilterOption {
  id: string;
  name: string;
}

const HomePage: React.FC<IClientUser> = (props: IClientUser) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFields, setSelectedFields] = useState<FilterOption[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<FilterOption[]>([]);
  const [recentFields, setRecentFields] = useState<FilterOption[]>([]);
  const [recentLocations, setRecentLocations] = useState<FilterOption[]>([]);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [showMoreLocations, setShowMoreLocations] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const history = useHistory();
  const { data: searchProjectsData } = useQuery(GET_PROJECTS_BY_FILTER, { variables: { filter: searchValue }, skip: !searchValue.trim() });
  const [getGenres, { data: genresData }] = useLazyQuery(GET_ALL_GENRES);
  const [getLocations, { data: locationsData }] = useLazyQuery(GET_ALL_LOCATIONS);
  const [recentPeople] = useState<any[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [activeFilter, setActiveFilter] = useState('all');

  const allLocations: FilterOption[] = locationsData?.getAllLocations?.map(
    (loc: any) => ({
      id: loc.id,
      name: loc.name
    })
  ) || [];

  const allFields: FilterOption[] = genresData?.getAllGenres?.map(
    (genre: any) => ({
      id: genre.id,
      name: genre.name
    })
  ) || [];

  const genreIconMap: Record<string, string> = {
    Video: iconFilm,
    Photography: iconCinema,
    Multimedia: iconCamera,
    Music: iconComposer,
    Digital: iconUXUI,
    DanceAndTheater: iconCostume,
    Games: iconGame,
    ArtsAndCrafts: iconPalette,
    SocialImpact: iconLove,
    DesignAndIllustration: iconPen
  };

  const formatGenreName = (name: string): string => {
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  const displayedFields = showMoreFields ? allFields : allFields.slice(0, 5);
  const displayedLocations = showMoreLocations ? allLocations : allLocations.slice(0, 5);
  const { data: usersData } = useQuery(GET_ALL_USERS);
  const allUsers = usersData?.getAllUsers || [];

  const filteredProjects = searchProjectsData?.getProjectsByFilter || [];

  const filteredUsers = searchValue.trim()
    ? allUsers.filter(user =>
      user.profile?.name?.toLowerCase().includes(searchValue.toLowerCase())
    ).slice(0, 5)
    : [];

  const handleFilterClick = () => {
    if (isFilterExpanded) {
      handleFilterClose();
    } else {
      setIsFilterExpanded(true);
      getGenres();
      getLocations();
    }
  };

  const handleFilterClose = () => {
    setIsFilterExpanded(false);

    selectedFields.forEach(field => {
      setRecentFields(prev => {
        const filtered = prev.filter(f => f.id !== field.id);
        return [field, ...filtered].slice(0, 5);
      });
    });

    selectedLocations.forEach(location => {
      setRecentLocations(prev => {
        const filtered = prev.filter(l => l.id !== location.id);
        return [location, ...filtered].slice(0, 5);
      });
    });
  };

  const handleSearchFocus = () => {
    setShowAutocomplete(true);
    if (!genresData) getGenres();
    if (!locationsData) getLocations();
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowAutocomplete(false);
    }, 200);
  };

  const handleFieldToggle = (field: FilterOption) => {
    setSelectedFields(prev =>
      prev.some(f => f.id === field.id)
        ? prev.filter(f => f.id !== field.id)
        : [...prev, field]
    );
  };

  const handleLocationToggle = (location: FilterOption) => {
    setSelectedLocations(prev =>
      prev.some(l => l.id === location.id)
        ? prev.filter(l => l.id !== location.id)
        : [...prev, location]
    );
  };

  const isFieldSelected = (field: FilterOption) =>
    selectedFields.some(f => f.id === field.id);
  const isLocationSelected = (location: FilterOption) =>
    selectedLocations.some(l => l.id === location.id);

  const activeFiltersCount = selectedFields.length + selectedLocations.length;

  return (
    <main className="page-project-list">
      <Navigation {...props} />
      <section className="container">
        <div className="columns section">
          <div className="column page-feed-sidebar">
            <div className="page-feed-sidebar-greeting">
              Welcome back, {props.user?.profile?.name ?? 'User'}!

              {/* {props?.user?.verified &&
                props?.user?.stripeAccountStatus !== 'complete' && (
                  <>
                    <br />
                    <a href="/stripe-onboard">
                      {props?.user?.stripeAccountStatus === 'incomplete'
                        ? 'Continue Onboarding'
                        : 'Onboard with Stripe'}
                    </a>
                  </>
                )}

              {props?.user?.stripeAccountStatus === 'complete' && (
                <>
                  <br />
                  <a
                    href=""
                    onClick={async (e: any) => {
                      e.preventDefault();

                      const res = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/api/v1/stripe/login-link?accountId=${props.user?.stripeId}`,
                        {
                          method: 'GET',
                          headers: {
                            Authorization: `Bearer ${accessTokenStorage.get()}`
                          }
                        }
                      );

                      const data = await res.json();

                      if (data.error) {
                        window.alert(data.message);
                        return;
                      }

                      window.open(data.url, '_blank');
                    }}
                  >
                    Stripe Login
                  </a>
                </>
              )} */}
            </div>
            <p className="page-feed-sidebar-description is-hidden-mobile">
              This is your creative space to share, discover, and celebrate work across film, music, and games
            </p>

            <nav className="page-feed-sidebar-menu">
              <button
                className={`page-feed-sidebar-menu-item ${activeFilter === 'all' ? 'is-active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                <img src={notificationIcon} alt="" className="page-feed-sidebar-menu-item-icon" />
                <span>All</span>
              </button>

              <button
                className={`page-feed-sidebar-menu-item ${activeFilter === 'videos' ? 'is-active' : ''}`}
                onClick={() => setActiveFilter('videos')}
              >
                <img src={videoCameraIcon} alt="" className="page-feed-sidebar-menu-item-icon" />
                <span>Videos</span>
              </button>

              <button
                className={`page-feed-sidebar-menu-item ${activeFilter === 'projects' ? 'is-active' : ''}`}
                onClick={() => setActiveFilter('projects')}
              >
                <img src={folderIcon} alt="" className="page-feed-sidebar-menu-item-icon" />
                <span>Projects</span>
              </button>
            </nav>
          </div>

          <div className="column is-half page-project-list-column">
            <AssistantChat />
            <div className={`page-project-list-search-container ${isFilterExpanded ? 'is-expanded' : 'is-collapsed'}`}>

              {/* Search row */}
              <div className="page-project-list-search-row">

                {/* Search input */}
                <div className={`page-project-list-search-input-wrapper ${isFilterExpanded ? 'is-expanded' : 'is-collapsed'}`}>
                  <img src={searchIcon} alt="search" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Field, Name, Location"
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      if (selectedUserId) setSelectedUserId(null);
                      if (!isFilterExpanded && e.target.value.trim()) {
                        setShowAutocomplete(true);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowAutocomplete(false);
                        searchInputRef.current?.blur();
                      }
                    }}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                  />
                </div>

                {/* Filter button */}
                <button onClick={handleFilterClick} className="page-project-list-filter-button">
                  {activeFiltersCount > 0 && (
                    <span className="page-project-list-filter-badge">
                      {activeFiltersCount}
                    </span>
                  )}
                  <img src={filterIcon} alt="filter" />
                </button>
              </div>

              {/* Autocomplete dropdown */}
              {showAutocomplete && !isFilterExpanded && searchValue.trim() && (
                <div className="page-project-list-autocomplete" onMouseDown={(e) => e.preventDefault()}>
                  {searchValue.trim() ? (
                    <>
                      {filteredUsers.length === 0 && filteredProjects.length === 0 ? (
                        <div className="page-project-list-autocomplete-empty">
                          No results found
                        </div>
                      ) : (
                        <div className="page-project-list-autocomplete-list">
                          {filteredUsers.length > 0 && <div style={{ padding: '8px 16px', fontWeight: 'bold', fontSize: '12px', color: '#666', background: '#f5f5f5' }}>PEOPLE</div>}
                          {filteredUsers.map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                history.push('/user/' + user.id);
                              }}
                              className="page-project-list-user-item"
                            >
                              {user.avatar?.fileUrl ? (
                                <img
                                  src={user.avatar.fileUrl}
                                  alt={user.profile?.name}
                                  className="page-project-list-user-item-avatar"
                                />
                              ) : (
                                <div className="page-project-list-user-item-placeholder">
                                  {user.profile?.name?.charAt(0).toUpperCase() || '?'}
                                </div>
                              )}
                              <div className="page-project-list-user-item-info">
                                <div className="page-project-list-user-item-name">
                                  {user.profile?.name}
                                </div>
                                {user.professions && user.professions.length > 0 && (
                                  <div className="page-project-list-user-item-profession">
                                    {user.professions.map(p => p.name).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {filteredProjects.length > 0 && <div style={{ padding: '8px 16px', fontWeight: 'bold', fontSize: '12px', color: '#666', background: '#f5f5f5' }}>PROJECTS</div>}
                          {filteredProjects.map((project: any) => (
                            <div
                              key={project.id}
                              onClick={() => {
                                history.push('/project/' + project.id);
                              }}
                              className="page-project-list-user-item"
                            >
                              {project.medias && project.medias.length > 0 ? (
                                <img
                                  src={project.medias[0].fileUrl}
                                  alt={project.title}
                                  className="page-project-list-user-item-avatar"
                                />
                              ) : (
                                <div className="page-project-list-user-item-placeholder">
                                  P
                                </div>
                              )}
                              <div className="page-project-list-user-item-info">
                                <div className="page-project-list-user-item-name">
                                  {project.title}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="page-project-list-autocomplete-empty" />
                  )}
                </div>
              )}

              {/* Filter expanded content */}
              {isFilterExpanded && (
                <>
                  {(recentPeople.length > 0 || recentFields.length > 0 || recentLocations.length > 0) && (
                    <div className="page-project-list-recent-section">
                      {/* Recent People */}
                      {recentPeople.length > 0 && (
                        <div className="page-project-list-recent-people">
                          {recentPeople.map((person) => (
                            <div
                              key={person.id}
                              onClick={() => {
                                setSelectedUserId(person.id);
                                setSearchValue(person.profile?.name || '');
                                setIsFilterExpanded(false);
                              }}
                              className="page-project-list-user-item is-recent"
                            >
                              {person.avatar?.fileUrl ? (
                                <img
                                  src={person.avatar.fileUrl}
                                  alt={person.profile?.name}
                                  className="page-project-list-user-item-avatar is-large"
                                />
                              ) : (
                                <div className="page-project-list-user-item-placeholder is-large">
                                  {person.profile?.name?.charAt(0).toUpperCase() || '?'}
                                </div>
                              )}
                              <div className="page-project-list-user-item-info">
                                <div className="page-project-list-user-item-name is-large">
                                  {person.profile?.name}
                                </div>
                                {person.professions && person.professions.length > 0 && (
                                  <div className="page-project-list-user-item-profession is-large">
                                    {person.professions.map(p => p.name).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {(recentFields.length > 0 || recentLocations.length > 0) && (
                        <>
                          <h3 className="page-project-list-recent-section-title">Recent</h3>

                          <div className="page-project-list-recent-section-list">
                            {/* Recent Fields */}
                            {recentFields.map((field) => {
                              const selected = isFieldSelected(field);
                              return (
                                <button
                                  key={field.id}
                                  onClick={() => handleFieldToggle(field)}
                                  className={`page-project-list-filter-pill is-field ${selected ? 'is-selected' : ''}`}
                                >
                                  {genreIconMap[field.name] && (
                                    <img src={genreIconMap[field.name]} alt="" />
                                  )}
                                  {formatGenreName(field.name)}
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRecentFields(prev => prev.filter(f => f.id !== field.id));
                                    }}
                                    className="page-project-list-filter-pill-close"
                                  >
                                    ×
                                  </span>
                                </button>
                              );
                            })}

                            {/* Recent Locations */}
                            {recentLocations.map((location) => {
                              const selected = isLocationSelected(location);
                              return (
                                <button
                                  key={location.id}
                                  onClick={() => handleLocationToggle(location)}
                                  className={`page-project-list-filter-pill is-location ${selected ? 'is-selected' : ''}`}
                                >
                                  <img src={pinIcon} alt="location" />
                                  {location.name}
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRecentLocations(prev => prev.filter(l => l.id !== location.id));
                                    }}
                                    className="page-project-list-filter-pill-close"
                                  >
                                    ×
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Fields Section */}
                  <div className="page-project-list-filter-section">
                    <h3 className="page-project-list-filter-section-title">Fields</h3>

                    <div className="page-project-list-filter-section-items">
                      {displayedFields.map((field) => {
                        const selected = isFieldSelected(field);
                        return (
                          <button
                            key={field.id}
                            onClick={() => handleFieldToggle(field)}
                            className={`page-project-list-filter-pill is-field ${selected ? 'is-selected' : ''}`}
                          >
                            {genreIconMap[field.name] && (
                              <img src={genreIconMap[field.name]} alt="" />
                            )}
                            {formatGenreName(field.name)}
                            {selected && (
                              <span className="page-project-list-filter-pill-close">×</span>
                            )}
                          </button>
                        );
                      })}
                      {allFields.length > 5 && (
                        <button
                          onClick={() => setShowMoreFields(!showMoreFields)}
                          className="page-project-list-show-more"
                        >
                          {showMoreFields ? 'Show Less' : 'Show More +'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Locations Section */}
                  <div className="page-project-list-filter-section">
                    <h3 className="page-project-list-filter-section-title">Location</h3>
                    <div className="page-project-list-filter-section-items">
                      {displayedLocations.map((location) => {
                        const selected = isLocationSelected(location);
                        return (
                          <button
                            key={location.id}
                            onClick={() => handleLocationToggle(location)}
                            className={`page-project-list-filter-pill is-location ${selected ? 'is-selected' : ''}`}
                          >
                            <img src={pinIcon} alt="location" />
                            {location.name}
                            {selected && (
                              <span className="page-project-list-filter-pill-close">×</span>
                            )}
                          </button>
                        );
                      })}
                      {allLocations.length > 5 && (
                        <button
                          onClick={() => setShowMoreLocations(!showMoreLocations)}
                          className="page-project-list-show-more"
                        >
                          {showMoreLocations ? 'Show Less' : 'Show More +'}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Active Filters Display - Only when collapsed */}
            {!isFilterExpanded && (selectedFields.length > 0 || selectedLocations.length > 0) && (
              <div className="page-project-list-active-filters">
                {selectedFields.map(field => (
                  <span key={field.id} className="page-project-list-active-filters-chip is-field">
                    {genreIconMap[field.name] && (
                      <img src={genreIconMap[field.name]} alt="" />
                    )}
                    {formatGenreName(field.name)}
                    <button
                      onClick={() => handleFieldToggle(field)}
                      className="page-project-list-active-filters-chip-close"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedLocations.map(location => (
                  <span key={location.id} className="page-project-list-active-filters-chip is-location">
                    <img src={pinIcon} alt="location" />
                    {location.name}
                    <button
                      onClick={() => handleLocationToggle(location)}
                      className="page-project-list-active-filters-chip-close"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <FilteredProjectListContextProvider
              searchValue={''}
              selectedFields={selectedFields}
              selectedLocations={selectedLocations}
              selectedUserId={selectedUserId}
              activeFilter={activeFilter}
            >
              <ProjectList
                user={props.user as any}
                currentUserId={props.user?.id}
              />
            </FilteredProjectListContextProvider>
          </div>
          <div className="column page-project-list-sidebar">
            <div className="card page-project-list-sidebar-card is-dropshadowed is-rounded">
              <header className="card-header is-bordered-bottom p-t-2 m-b-0">
                <div className="card-header-title is-table has-text-centered">
                  <img src={feedbackImg} alt="feedback" />
                  <p className="is-size-5 m-b-1 has-text-weight-bold">
                    Feedback
                  </p>
                  <p className="content is-small is-light">
                    Would love to hear your feedback so that we can make
                    MatchHat awesome!
                  </p>
                  <button
                    className="button has-background-grey is-borderless is-bordered-radius"
                    onClick={() => {
                      window.open(
                        'https://docs.google.com/forms/d/e/1FAIpQLSe--GSi8vWGLyeSXUeGmmVJG_vXKKWxemUp7FsXcNtPKXwopw/viewform'
                      );
                    }}
                  >
                    <span className="is-small has-text-primary has-text-weight-semibold">
                      Share Feedback
                    </span>
                  </button>
                </div>
              </header>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
